import { IconButton, LabelAndIconButton, Menu, MenuItem } from '@biq/apex-ui';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Typography } from '@mui/material';
import React, { useState } from 'react';
import {
    DOWNLOAD_BODY_TEXT,
    DOWNLOAD_BUTTON_TEXT,
    DOWNLOADED_BODY_TEXT,
    DOWNLOADED_BUTTON_TEXT,
    DownloadJobMenuStyles,
} from './download-job-menu-helpers';

interface DownloadJobMenuProps {
    jobDownloadId: string | undefined;
    openJobId: string | undefined;
    isDownloadEnabled: boolean;
    onMenuOpen: any;
    downloadSelectedJob: (id: string | undefined) => void;
}
const DownloadJobMenu = ({
    jobDownloadId,
    openJobId,
    isDownloadEnabled,
    onMenuOpen,
    downloadSelectedJob,
}: DownloadJobMenuProps): React.JSX.Element => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const menuBodyText: string = isDownloadEnabled
        ? DOWNLOAD_BODY_TEXT
        : DOWNLOADED_BODY_TEXT;
    const buttonText: string = isDownloadEnabled
        ? DOWNLOAD_BUTTON_TEXT
        : DOWNLOADED_BUTTON_TEXT;
    const open: boolean = openJobId === jobDownloadId && Boolean(anchorEl);

    const handleClose = (): void => {
        setAnchorEl(null);
        onMenuOpen(undefined);
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
        event.stopPropagation();

        // if the jobID is the same as the openJobId, we are clicking on the same menu hence we need to close it.
        if (openJobId === jobDownloadId) {
            handleClose();
        } else {
            // if the jobID is different as the openJobId, we are opening a new job menu item.
            setAnchorEl(event.currentTarget);
            onMenuOpen(jobDownloadId);
        }
    };

    const handleDownloadClick = (): void => {
        downloadSelectedJob(openJobId);
        handleClose();
    };

    return (
        <Box sx={DownloadJobMenuStyles.mainContainer}>
            <IconButton
                aria-label='more'
                id='job-download-button'
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup='true'
                onClick={handleClick}
            >
                <MoreVertIcon />
            </IconButton>
            <Menu
                id='job-download-menu'
                MenuListProps={{
                    'aria-labelledby': 'job-download-button',
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                sx={DownloadJobMenuStyles.menu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem sx={DownloadJobMenuStyles.menuItem}>
                    <Box sx={DownloadJobMenuStyles.menuItemContainer}>
                        <Box
                            className='header-container'
                            sx={DownloadJobMenuStyles.sectionsContainer}
                        >
                            <IconButton
                                color='grayalt'
                                size='small'
                                buttonType='closeButton'
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Box className='header'>
                                <Typography sx={DownloadJobMenuStyles.title}>
                                    Download File
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={DownloadJobMenuStyles.messageContainer}>
                            <Typography sx={DownloadJobMenuStyles.message}>
                                {menuBodyText}
                            </Typography>
                        </Box>
                        <Box sx={DownloadJobMenuStyles.sectionsContainer}>
                            <LabelAndIconButton
                                color='primary'
                                variant='contained'
                                position='start'
                                icon={<DownloadIcon />}
                                onClick={handleDownloadClick}
                                disabled={!isDownloadEnabled}
                            >
                                {buttonText}
                            </LabelAndIconButton>
                        </Box>
                    </Box>
                </MenuItem>
            </Menu>
        </Box>
    );
};

export default DownloadJobMenu;
